import type { Dispatch, SetStateAction } from 'react'
import { useMemo } from 'react'

import { ComparatorModal } from '@/components/modals/ComparatorModal'
import { PatientConsentModal } from '@/components/modals/PatientConsentModal'
import { getFulfillmentStrategy, getTrfOrgAddress } from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import type { ComparatorParentConsentConfigSlice } from '@/components/order/comparatorParentConsentConfig'
import type { CanonicalOrderPayload, Comparator } from '@/ordering/canonicalOrder'
import { getParentComparator } from '@/ordering/comparatorReadAdapter'
import {
  ADD_FAMILY_MEMBER_RELATIONSHIP_OPTIONS,
  relationshipOptionsForStrategy,
  type COMPARATOR_RELATIONSHIP_OPTIONS,
} from '@/ordering/comparatorRelationshipOptions'
import { parentSideForComparator } from '@/ordering/comparatorWriteAdapter'
import type { ComparatorSectionEditState } from '@/pages/order/orderSideNavModel'
import { comparatorClinicalNoteBudget } from '@/utils/freeTextValidation'
import {
  mapCollectionStringToSampleTypeStrict,
  type SampleTypeOption,
} from '@/utils/orderFormUtils'

/**
 * Section-editor validation rules a page can enforce on the R5 side-nav
 * Demographics / Clinical / Consent editor.
 *
 * OrderInProgress (submitted) supplies a profile mirroring its legacy
 * pencil — `mrnRequired` + `requireClinicalIdentity` + per-side
 * research-contact obligation — so a parent edited through the side-nav
 * can't be saved with looser rules than the page enforces elsewhere
 * (the previous behavior left the section silently "needs attention").
 *
 * OrderStart (draft) omits this prop, preserving its current draft
 * leniency.
 */
export interface ComparatorSectionValidationProfile {
  /** Section editor must enforce MRN before save (mirrors the legacy parent pencil). */
  mrnRequired?: boolean
  /** Section editor must enforce DOB / phenotypic trait. */
  requireClinicalIdentity?: boolean
  /** Patient research opt-in is on (drives the contact-channel prompt). */
  patientResearchContactOptIn?: boolean
  /**
   * Patient research opt-in is on AND should obligate the primary-guardian
   * comparator (any relationship, testing or not) to supply a contact channel.
   * The modal ANDs this with the row's own primary-guardian flag. Distinct from
   * `appliesToThisParent`, which is parent-slot-scoped and testing-gated.
   */
  probandResearchContactConsent?: boolean
  /**
   * Per-side resolver: does the patient research contact opt-in obligate
   * THIS comparator row to provide a contact channel? Only meaningful when
   * the comparator is a parent (mother / father). Returning `undefined`
   * leaves the prop unset.
   */
  appliesToThisParent?: (side: 'mother' | 'father') => boolean
}

export interface ComparatorSideNavModalsProps {
  /** Current canonical order (used to find the section-edit comparator + enrich from legacy slot). */
  storedCanonical: CanonicalOrderPayload | null | undefined
  /** Org-applicable proband consent configs. Drives the consent block + Patient Consent modal. */
  comparatorParentConsentConfigs: ComparatorParentConsentConfigSlice
  /**
   * Raw collection strings from the active test type config. Mapped + filtered
   * to `SampleTypeOption[]` once and reused for all three modal mounts (the
   * pages used to repeat this map at every mount site).
   */
  parentSampleTypes: ReadonlyArray<string | null | undefined> | null | undefined

  /**
   * Collection-kit ordering gate (`config.featureFlags?.kitOrdering`, resolved
   * per page via `isKitOrderingEnabled`). When true, the add + section-editor
   * modals expose the "Request collection kit" toggle + shipment-address
   * sub-form. When false/missing, the kit-request UI is hidden. Defaults to
   * false so kit ordering is opt-in.
   */
  kitOrderingEnabled?: boolean

  /**
   * Epic launch flow flag. When true (and `epicState` resolves to a live Epic
   * session), the Add-Comparator create step and the Demographics section
   * editor expose the EHR patient-demographics MRN lookup (search button +
   * auto lookup on first/last/DOB). Standalone (web) launches omit this.
   */
  isEpicFlow?: boolean
  /** Epic SMART OAuth state key used to locate the session for the MRN lookup. */
  epicState?: string

  // ── Add Comparator
  addComparatorOpen: boolean
  setAddComparatorOpen: Dispatch<SetStateAction<boolean>>
  addComparatorRelationshipOptions: typeof COMPARATOR_RELATIONSHIP_OPTIONS
  /** Persists the new comparator (id + selection are handled by the hook). */
  onAddComparatorSave: (comparator: Comparator) => void

  // ── Section editor (Demographics / Clinical / Consent)
  comparatorSectionEdit: ComparatorSectionEditState | null
  setComparatorSectionEdit: Dispatch<SetStateAction<ComparatorSectionEditState | null>>
  /** Persists the focused-section edit. */
  onComparatorSectionSave: (comparator: Comparator) => void
  /**
   * Optional per-page validation profile applied to the section-edit modal.
   * Pass on submitted orders so the side-nav demographics editor enforces the
   * same MRN / research-contact rules as the legacy pencil.
   */
  validationProfile?: ComparatorSectionValidationProfile

  /**
   * Cell D — when `true`, the focused section editor suppresses HPO/phenotype
   * editing (the section-edit comparator is always an EXISTING/persisted row,
   * so this maps directly to `canEditExistingMemberHpo === false` on a locked
   * inflection_test order). Other clinical fields stay editable. Defaults to
   * `false`. The slim "Add Comparator" create step is unaffected (it has no
   * clinical/HPO block), preserving newly-added HPO editability (rule 7).
   */
  suppressExistingComparatorHpoEditing?: boolean

  // ── Patient Consent
  patientConsentModalOpen: boolean
  setPatientConsentModalOpen: Dispatch<SetStateAction<boolean>>
  /** Persists patient-side consent edits to the canonical order. */
  onPatientConsentSave: (patch: NonNullable<CanonicalOrderPayload['patient']>) => void
  /**
   * When true, the patient Consent modal surfaces inline validation errors on
   * unanswered `requireInteraction` consent sections. Pages pass their proband
   * "needs attention" state (set after a blocked submit / opening the section
   * from a needs-attention card) so a pristine, first-time-open editor does NOT
   * render pre-emptive red errors. Defaults to false.
   */
  patientConsentShowValidationErrors?: boolean
}

/**
 * Shared mount point for the R5 side-nav comparator modals: the slim
 * "Add Comparator" create step, the focused section editor (Demographics
 * → Clinical History → Consent chain), and the patient Consent modal.
 *
 * The three mounts were duplicated near-identically between OrderStart and
 * OrderInProgress; consolidating here removes that drift surface and keeps
 * the demographics-editor validation profile in one place.
 */
export function ComparatorSideNavModals({
  storedCanonical,
  comparatorParentConsentConfigs,
  parentSampleTypes,
  kitOrderingEnabled = false,
  isEpicFlow = false,
  epicState,
  addComparatorOpen,
  setAddComparatorOpen,
  addComparatorRelationshipOptions,
  onAddComparatorSave,
  comparatorSectionEdit,
  setComparatorSectionEdit,
  onComparatorSectionSave,
  validationProfile,
  suppressExistingComparatorHpoEditing = false,
  patientConsentModalOpen,
  setPatientConsentModalOpen,
  onPatientConsentSave,
  patientConsentShowValidationErrors = false,
}: ComparatorSideNavModalsProps) {
  /**
   * Map collection strings → SampleTypeOption[] once. Both pages used to
   * repeat this `?.map(...).filter(...)` at every mount site.
   */
  const parentSampleTypeOptions = useMemo<ReadonlyArray<SampleTypeOption> | undefined>(
    () => {
      if (!parentSampleTypes) return undefined
      return parentSampleTypes
        .map((c) => mapCollectionStringToSampleTypeStrict(c ?? null))
        .filter((s): s is SampleTypeOption => s !== null)
    },
    [parentSampleTypes]
  )

  // GEN-4933: org mailing address (microapp CONTACTS → `UiConfig.orgAddress`)
  // used to pre-fill a Clinic / site kit shipment address. Sourced from the
  // already-loaded UI config — no extra fetch.
  const { config } = useUiConfig()
  const labAddress = useMemo(() => getTrfOrgAddress(config), [config])

  // GEN-2997/3152: the Demographics section editor exposes an EDITABLE
  // relationship select (it is not `legacyFixedParentDefaults`-locked like the
  // parent pencil), so its option list must respect the fulfillment strategy.
  // For `inflection_test` (LSMC / SeqOne) this collapses to MOTHER / FATHER,
  // matching the "Add family member" pickers; `rcigm` keeps the full list.
  // Without this, editing an existing non-parent comparator's demographics on
  // an `inflection_test` order would surface the extended relationship list.
  const sectionEditRelationshipOptions = useMemo(
    () =>
      relationshipOptionsForStrategy(
        getFulfillmentStrategy(config),
        ADD_FAMILY_MEMBER_RELATIONSHIP_OPTIONS
      ),
    [config]
  )

  // Guardian → comparator demographic pre-fill source. Passed to the Add
  // Comparator create step (fills empty fields the moment a matching unique
  // relationship is picked) and the Demographics section editor (fills on
  // open). The modal gates on relationship match + uniqueness and only fills
  // empty fields, so passing it whenever a guardian contact exists is safe (a
  // non-matching relationship is a no-op).
  const guardianPrefill = useMemo(() => {
    const g = storedCanonical?.guardianContact
    if (!g) return undefined
    return {
      relationshipToPatient: g.relationshipToPatient ?? null,
      firstName: g.firstName ?? null,
      lastName: g.lastName ?? null,
      dateOfBirth: g.dateOfBirth ?? null,
      email: g.email ?? null,
      phone: g.phone ?? null,
    }
  }, [storedCanonical?.guardianContact])

  return (
    <>
      <PatientConsentModal
        isOpen={patientConsentModalOpen}
        onClose={() => setPatientConsentModalOpen(false)}
        configs={comparatorParentConsentConfigs}
        patient={storedCanonical?.patient}
        comparators={storedCanonical?.comparators}
        onSave={onPatientConsentSave}
        showValidationErrors={patientConsentShowValidationErrors}
      />

      {/* R5 (side-nav comparator detail): slim "Add Comparator" create step.
          Only relationship + sample type + primary guardian; demographics,
          clinical history, and consent are filled afterward from the new
          comparator's detail view. The shared hook handles id assignment,
          modal close, and selection. */}
      {addComparatorOpen ? (
        <ComparatorModal
          isOpen={true}
          onClose={() => setAddComparatorOpen(false)}
          fieldGroups="create"
          relationshipOptions={addComparatorRelationshipOptions}
          enableSampleType
          enableKitRequest={kitOrderingEnabled}
          parentSampleTypes={parentSampleTypeOptions}
          labAddress={labAddress}
          probandIncidentalFindingsSelection={storedCanonical?.patient?.incidentalFindingsSelection ?? null}
          onSave={onAddComparatorSave}
          {...(guardianPrefill ? { guardianPrefill } : {})}
        />
      ) : null}

      {/* R5: focused editor for one of the comparator detail sections
          (Demographics / Clinical history / Consent). Reuses <ComparatorModal>
          restricted to the chosen field group; saves upsert the comparator
          in place. Each section saves and closes via the standard
          "Discard Changes / Done" footer — matching the other in-progress
          section modals — rather than the "Continue to next section →"
          wizard. */}
      {comparatorSectionEdit
        ? (() => {
            const sectionComparator = (storedCanonical?.comparators ?? []).find(
              (c) => c?.id === comparatorSectionEdit.id
            )
            if (!sectionComparator) return null
            // Plan C (C-read-3): resolve a parent through the read adapter so
            // Sample Type + consent toggles hydrate (the comparator row may be
            // demographics-only). Slot enrichment stays at the adapter boundary.
            // The resolver matches by side, so it returns the same row as the
            // id-found `sectionComparator` only under the MOTHER / FATHER
            // 1-of-1 invariant; `?? sectionComparator` is a defensive fallback.
            const sectionSide = parentSideForComparator(sectionComparator)
            const sectionInitial = sectionSide
              ? getParentComparator(storedCanonical, sectionSide) ?? sectionComparator
              : sectionComparator

            // Per-side research-contact obligation only applies to parent rows;
            // the resolver returns `undefined` for non-parent comparators so
            // the prop stays unset.
            const appliesToThisParent =
              validationProfile?.appliesToThisParent && sectionSide
                ? validationProfile.appliesToThisParent(sectionSide)
                : undefined

            // Guardian → comparator demographic pre-fill: only for the
            // Demographics section editor (fills on open). Uses the shared
            // `guardianPrefill` memo above; the modal gates on relationship
            // match + uniqueness and fills empty fields only, so a non-matching
            // relationship is a no-op.
            const sectionGuardianPrefill =
              comparatorSectionEdit.group === 'demographics' ? guardianPrefill : undefined

            return (
              <ComparatorModal
                isOpen={true}
                onClose={() => setComparatorSectionEdit(null)}
                fieldGroups={comparatorSectionEdit.group}
                initialData={sectionInitial}
                clinicalNoteBudget={comparatorClinicalNoteBudget(
                  getFulfillmentStrategy(config),
                  storedCanonical,
                  sectionInitial?.id
                )}
                relationshipOptions={sectionEditRelationshipOptions}
                comparatorConsentConfig={comparatorParentConsentConfigs}
                enableClinicalFields
                suppressHpoEditing={suppressExistingComparatorHpoEditing}
                enableSampleType
                enableKitRequest={kitOrderingEnabled}
                parentSampleTypes={parentSampleTypeOptions}
                labAddress={labAddress}
                probandIncidentalFindingsSelection={storedCanonical?.patient?.incidentalFindingsSelection ?? null}
                onSave={onComparatorSectionSave}
                autosave
                isEpicFlow={isEpicFlow}
                epicState={epicState}
                {...(sectionGuardianPrefill ? { guardianPrefill: sectionGuardianPrefill } : {})}
                {...(validationProfile?.mrnRequired ? { mrnRequired: true } : {})}
                {...(validationProfile?.requireClinicalIdentity
                  ? { requireClinicalIdentity: true }
                  : {})}
                {...(validationProfile?.patientResearchContactOptIn
                  ? { patientResearchContactOptIn: true }
                  : {})}
                {...(appliesToThisParent !== undefined
                  ? { patientResearchContactAppliesToThisParent: appliesToThisParent }
                  : {})}
                {...(validationProfile?.probandResearchContactConsent
                  ? { probandResearchContactPrimaryGuardian: true }
                  : {})}
              />
            )
          })()
        : null}
    </>
  )
}
